import React from 'react'
import moment from 'moment'
import Card from 'src/components/riders/rider-portal/common/Card'
import { IService } from 'src/types'
import { PORTAL_DAYS_MAPPING, RIDER_PORTAL_UI_MESSAGES } from 'src/constants/rider-portal'

interface IServiceItemProps {
  service: IService
  selectedService: IService | null
  handleSelectService: (service: IService) => void
}

interface Schedule {
  id: string
  from: string // "HH:mm"
  until: string // "HH:mm"
  days: string[] // ['mon', 'tue', ...]
  serviceId: string
}

const ServiceItem = ({ selectedService, service, handleSelectService }: IServiceItemProps) => {
  const { description, isAllDaysActive, name, serviceDaysHours } = service
  const { dataFeedback, trips } = RIDER_PORTAL_UI_MESSAGES

  const checkServiceSchedule = () => {
    // Service is available 24/7
    if (isAllDaysActive) {
      return false
    }

    // Service is not available 24/7 and has certain schedules
    const today = moment()
      .format('ddd')
      .toLowerCase()
    const currentHour = moment().format('HH:mm')
    const filterSchedule = serviceDaysHours.filter(sched => {
      const { days, from, until } = sched
      if (days.includes(today as 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun')) {
        const start = moment(from, 'HH:mm')
        const end = moment(until, 'HH:mm')
        const isOpen = moment(currentHour, 'HH:mm').isBetween(start, end, undefined, '[]')
        if (isOpen) {
          return sched
        }
      }
      return
    })
    return filterSchedule.length === 0
  }

  const getNextAvailableSchedule = (schedules: Schedule[]): { day: string; from: string; until: string } | null => {
    const now = new Date()
    const currentTime = moment().format('HH:mm')
    const dayOrder = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']
    const availableSchedule: Array<{ date: Date; day: string; from: string; until: string }> = []

    for (let i = 0; i < 7; i++) {
      const date = new Date(now)
      date.setDate(now.getDate() + i)
      const day = dayOrder[date.getDay()]

      for (const schedule of schedules) {
        if (schedule.days.includes(day)) {
          if (i === 0 && schedule.from <= currentTime) {
            continue // Today, but already passed
          }
          availableSchedule.push({ date, day, from: schedule.from, until: schedule.until })
        }
      }
    }

    availableSchedule.sort((a, b) => {
      const aDateTime = new Date(a.date)
      const [aH, aM] = a.from.split(':').map(Number)
      aDateTime.setHours(aH, aM, 0, 0)

      const bDateTime = new Date(b.date)
      const [bH, bM] = b.from.split(':').map(Number)
      bDateTime.setHours(bH, bM, 0, 0)

      return aDateTime.getTime() - bDateTime.getTime()
    })

    if (availableSchedule.length === 0) {
      return null
    }

    return {
      day: availableSchedule[0].day,
      from: availableSchedule[0].from,
      until: availableSchedule[0].until
    }
  }

  const isServiceClose = checkServiceSchedule()
  const nextSchedule = isServiceClose ? getNextAvailableSchedule(serviceDaysHours) : null

  return (
    <div
      className={`rp-services-item ${isServiceClose ? 'opacity-70 pointer-events-none' : ''}`}
      role='button'
      tabIndex={0}
      onClick={() => handleSelectService(service)}
      onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter') {
          handleSelectService(service)
        }
      }}
    >
      <Card
        className='border-box cursor--pointer h100'
        active={selectedService ? selectedService.id === service.id : false}
        error={isServiceClose}
      >
        <h4 className='font-black1 font-medium my0 text-md'>{name}</h4>
        <p className='font-black1 mb0 mt03 text-sm'>{description || <i>{dataFeedback.noDescriptionAvailable}</i>}</p>
        {nextSchedule && (
          <p className='font-black1 font-medium mb0 mt03 text-sm'>
            {`${trips.nextSchedule}: ${
              PORTAL_DAYS_MAPPING[nextSchedule.day as keyof typeof PORTAL_DAYS_MAPPING]
            } from ${nextSchedule.from} to ${nextSchedule.until}`}
          </p>
        )}
        {(!isAllDaysActive || isAllDaysActive === null) && serviceDaysHours.length === 0 && (
          <p className='font-black1 font-medium mb0 mt03 text-sm'>{dataFeedback.noSchedulesFound}</p>
        )}
      </Card>
    </div>
  )
}

export default ServiceItem



// WEBPACK FOOTER //
// ./src/components/riders/rider-portal/trips/create/Step1/ServiceItem.tsx